import { useQuery } from '@tanstack/react-query';
import { Divider, Form } from 'antd';
import { SelectProps } from 'antd/lib';
import { FormInstance } from 'antd/lib/form';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CompanyContact from '@/model/General/CompanyContact';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

/**
 * Hook que gestiona la selección y validación de contactos de empresa
 * para el formulario de orden de servicio.
 */
const useCompanyContacts = (
  form: FormInstance | undefined,
  selectedCompany: number | undefined,
) => {
  const { t } = useTranslation();
  const { companyRepository } = useRepositoriesContext();
  const { data: companyContactData, isFetching: loadingCompanyContactData } = useQuery<
    CompanyContact[]
  >({
    queryKey: ['companyContacts', selectedCompany],
    queryFn: () => companyRepository.getCompanyContact(selectedCompany!),
    enabled: selectedCompany !== undefined,
  });
  const companyContact = Form.useWatch('company_contact', form);
  const [companyContactWarning, setCompanyContactWarning] = useState<string>();

  // Seteo de warning si no tiene contactos
  useEffect(() => {
    if (selectedCompany === undefined || loadingCompanyContactData) {
      return;
    }

    if (selectedCompany && companyContactData?.length === 0) {
      form?.setFields([
        {
          name: 'company',
          warnings: [t('La empresa o técnico no tiene un contacto asignado')],
        },
      ]);
    } else {
      form?.setFields([
        {
          name: 'company',
          warnings: [],
        },
      ]);
    }
  }, [selectedCompany, form, loadingCompanyContactData, t, companyContactData?.length]);

  // Warning si el contacto no tiene email
  useEffect(() => {
    if (!companyContactData || !companyContact) {
      setCompanyContactWarning(undefined);

      return;
    }

    const contactId = typeof companyContact === 'string' ? Number(companyContact) : companyContact;
    const selectedContact = [...(companyContactData ?? [])].find(
      contact => contact.id === contactId,
    );

    if (selectedContact && !selectedContact.email) {
      setCompanyContactWarning(t('El contacto seleccionado no tiene un email asignado'));
    } else {
      setCompanyContactWarning(undefined);
    }
  }, [companyContact, companyContactData, t]);

  // Armado de opciones de contacto
  const formatContactOption = (contact: CompanyContact) => ({
    label: (
      <>
        {contact.name}
        {contact.email ? (
          <>
            <Divider type="vertical" /> {contact.email}
          </>
        ) : null}
      </>
    ),
    value: contact.id,
  });
  const contactOptions = useMemo(() => {
    if (companyContactData?.length) {
      return companyContactData.map(formatContactOption);
    }
  }, [companyContactData]);
  const companyContactRules = useMemo(() => {
    return [
      {
        required: true,
        message: t('Debe seleccionar un contacto'),
      },
      {
        validator: () => {
          if (companyContactWarning) {
            return Promise.reject(new Error(companyContactWarning));
          }

          return Promise.resolve();
        },
      },
    ];
  }, [companyContactWarning, t]);
  const companyContactsSelectProps: SelectProps = useMemo(
    () => ({
      options: contactOptions,
      loading: loadingCompanyContactData,
      disabled: !selectedCompany || loadingCompanyContactData || !contactOptions?.length,
    }),
    [contactOptions, loadingCompanyContactData, selectedCompany],
  );

  return { companyContactsSelectProps, companyContactRules };
};

export default useCompanyContacts;
